/**
 * What to call a Sky dashboard request while it is in flight.
 *
 * The loading overlay watches the shared react-query cache and narrates what it
 * sees. Narration needs names, and a react-query key is not one: it is a
 * machine key built by `skyQueryKey` (`./skyEndpoints.ts`) and shaped
 * `['sky_social', name, { product, ...extra }]`. This module is the one place
 * that turns such a key into a sentence a human reads.
 *
 * WHY A REGISTRY AND NOT A LABEL ON EACH HOOK. A `label` field beside every
 * `queryKey` would be the obvious shape and the wrong one here: the overlay has
 * to name a query it observes from OUTSIDE, through the cache, where nothing
 * but the key survives. Anything the hook knows and the key does not is
 * invisible at the point of use.
 */

/**
 * The first element `skyQueryKey` writes, and the only value this module
 * accepts there.
 *
 * 🔴 MATCHED BY EQUALITY, NEVER BY PREFIX. `useSkyProducts` keys itself
 * `['sky_social_products']` (useSkyProducts.ts:75) — a different key that
 * merely starts with the same characters. It is the product CATALOGUE rather
 * than a panel's data, `staleTime: Infinity`, and it resolves before the page
 * has decided what to fetch. A `startsWith` here would fold it into the
 * progress denominator as a phantom step that completes before anything the
 * user is waiting for, which is a lie the bar cannot recover from.
 */
export const SKY_QUERY_ROOT = 'sky_social'

/**
 * Every `name` a production hook passes to `skyQueryKey`.
 *
 * Sourced by grepping `skyQueryKey(` under `src/hooks/`. Two entries here are
 * not on the product dashboards today — `entity_leaderboard` and `spend`
 * (the latter serves `ProductSpend.tsx`) — and they are registered anyway,
 * because they ARE `sky_social` keys and a page that later mounts them should
 * narrate them properly rather than fall through to the generic form.
 *
 * `chart-batch` is absent deliberately: it is handled below, where the key's
 * `chartIds` can name the actual charts in the batch.
 */
const NAME_LABELS: Record<string, string> = {
  entity_filter_summary: 'Filter options',
  record_metadata_summary: 'Comment metadata',
  campaign_tree: 'Campaign list',
  entity_metrics: 'Video metrics',
  cultural_relevance: 'Cultural relevance',
  'comment-count': 'Comment total',
  comments: 'Comment stream',
  'uncoded-disclosure': 'Uncoded comments',
  'platform-counts': 'Platform counts',
  entity_leaderboard: 'Video leaderboard',
  spend: 'Token spend',
}

/** The chart ids `pages/sky-social-media/charts.ts` defines, in reading order. */
const CHART_LABELS: Record<string, string> = {
  sentiment: 'Sentiment',
  emotion: 'Emotion',
  skyrelevant: 'Sky Relevant',
  skyattributed: 'Sky Attributed',
}

/** How many chart names to spell out before summarising the rest. */
const MAX_NAMED_CHARTS = 2

/**
 * Turn a machine name into something readable: `brand_new_panel` →
 * `Brand new panel`. This is what an unregistered name gets, so a hook added
 * tomorrow narrates itself on the day it lands instead of logging a raw key or
 * vanishing from the count.
 */
function titleCase(name: string): string {
  const words = name.replace(/[-_]+/g, ' ').trim()
  if (words === '') return 'Dashboard data'
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * A chart id is either a base id (`sentiment`) or a base id with a period
 * suffix (`sentiment-month`) — see `timeSeriesCharts` in
 * `pages/sky-social-media/charts.ts`. The suffix is what makes it a trend.
 */
function describeChartId(id: string): { label: string; overTime: boolean } {
  const trimmed = id.trim()
  const dash = trimmed.indexOf('-')
  if (dash > 0) {
    const base = trimmed.slice(0, dash)
    if (base in CHART_LABELS) return { label: CHART_LABELS[base], overTime: true }
  }
  return {
    label: CHART_LABELS[trimmed] ?? titleCase(trimmed),
    overTime: false,
  }
}

/**
 * Name a batched chart request by the charts inside it.
 *
 * `useSkyCharts` groups the page's chart definitions by what the server can
 * answer in one scan (`chartGroupKey`), so the number of requests — and which
 * charts ride in each — is decided at runtime. The key carries the membership
 * as a comma-joined `chartIds`, which is the only place the batch's contents
 * are legible from outside the hook.
 */
function labelForChartBatch(extra: Record<string, unknown> | undefined): string {
  const raw = extra?.chartIds
  const ids = typeof raw === 'string' ? raw.split(',').filter((s) => s.trim() !== '') : []
  if (ids.length === 0) return 'Chart data'

  const described = ids.map(describeChartId)
  const suffix = described.every((d) => d.overTime) ? ' over time' : ' breakdown'

  // De-duplicate: a batch of `sentiment-month` and `emotion-month` names two
  // charts, but one of `sentiment` and `sentiment-month` names one thing twice.
  const names: string[] = []
  for (const { label } of described) {
    if (!names.includes(label)) names.push(label)
  }

  if (names.length <= MAX_NAMED_CHARTS) {
    return `${joinWithAnd(names)}${suffix}`
  }
  const shown = names.slice(0, MAX_NAMED_CHARTS)
  const rest = names.length - MAX_NAMED_CHARTS
  return `${shown.join(', ')} and ${rest} more${suffix}`
}

function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/**
 * A human label for a react-query key, or `null` when the key is not one the
 * Sky loading overlay tracks.
 *
 * `null` means "not ours" and is what keeps the overlay's denominator honest —
 * every other feature's queries share the same cache, and counting them would
 * make the bar describe work the page is not waiting for.
 */
export function labelForSkyQuery(key: readonly unknown[]): string | null {
  if (key[0] !== SKY_QUERY_ROOT) return null

  const name = key[1]
  if (typeof name !== 'string' || name.trim() === '') return null

  const extra = (typeof key[2] === 'object' && key[2] !== null
    ? (key[2] as Record<string, unknown>)
    : undefined)

  if (name === 'chart-batch') return labelForChartBatch(extra)
  return NAME_LABELS[name] ?? titleCase(name)
}

export default labelForSkyQuery
